import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  Progress,
  SectionHeader,
  Typography,
} from 'web-library';
import queryString from 'query-string';
import { MutationStatus } from 'react-query';
import { candidateService } from '../../../../../api';
import PersonalDetails from '../PersonalDetails';
import SubjectSelectionTable from './SubjectSelectionTable';
import ExamDetails from '../ExamLevelDetails';
import { parseLocationSearch } from '../../../../../utils/StringUtils';
import {
  AppealDraftRequestBody,
  AppealDraftReviewParams,
  AppealSelectSubjectAppealParams,
} from '../../../../../api/candidate/appeal/model';
import RouteKey from '../../../../../routes/key';

const { appeal } = RouteKey.child;
const { reviewApplication, applications } = RouteKey.appeal.child;
const { generic } = RouteKey.error.child;
const { invalid } = RouteKey['unique-link'].child;
const { error: errorRoute, uniqueLink } = RouteKey.child;

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  dataTableContainer: {
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(7),
  },
  button: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
    },
  },
  errorText: {
    color: theme.color.state.stateError,
  },
}));

const SelectSubject = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const [selectionArr, setSelectionArr] = useState<number[]>([]);
  const [proceedStatus, setProceedStatus] = useState<MutationStatus>('idle');
  const [errMsg, setErrorMsg] = useState<boolean>(false);

  const postRequest = candidateService.appeal.candidate.usePostAppealDraft(
    ({ data }) => {
      setErrorMsg(false);
      setProceedStatus('success');
      const selectSubjectQueryParams: AppealDraftReviewParams = {
        appId: data.applicationId,
      };
      const selectSubjectParams = queryString.stringify(selectSubjectQueryParams);
      history.push(
        `/${appeal}/${applications.route}/${reviewApplication.route}?${selectSubjectParams}`
      );
    },
    () => {
      setErrorMsg(true);
      setProceedStatus('idle');

      const errorCode = error?.response?.data?.code;

      if (errorCode === '1019' || errorCode === '1014') {
        history.push(`/${uniqueLink}/${invalid}`);
      } else if (errorCode === '1203') {
        history.push(`/${errorRoute}/${generic}`);
      }
    }
  );

  const location = useLocation<AppealSelectSubjectAppealParams>();
  const { series = '', year = '', code = '', token = '' } = parseLocationSearch(location.search);

  const {
    data: examListingData,
    isLoading,
    error,
  } = candidateService.appeal.candidate.useGetExamListing(series, year, code, token);

  const handleProceedClick = () => {
    if (examListingData) {
      setProceedStatus('loading');
      const selectedSubjectCodes = selectionArr.map((subjectIndex) =>
        examListingData.subjectResultModels[subjectIndex].subjectCode.toString()
      );

      const mandatoryValues = {
        examCode: examListingData.examCode,
        examSeriesCode: examListingData.examSeriesCode,
        examYear: examListingData.examYear,
        subjectCodes: selectedSubjectCodes,
      };

      const requestBody: AppealDraftRequestBody = token
        ? {
          ...mandatoryValues,
          token,
        }
        : mandatoryValues;

      postRequest.mutate(requestBody);
    }
  };

  if (isLoading) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  if (error || !examListingData) {
    return <Typography>{t('appeal->>SelectSubject.Error getting access config data.')}</Typography>;
  }

  /** to handle go back previous page/action
   * */
  const onBack = () => {
    history.goBack();
  };

  return (
    <>
      {examListingData && (
        <Box>
          <ContentTemplate isContinuable type="full">
            <SectionHeader title={t('appeal->>SelectSubject.Select Subject(s)')} />
            <PersonalDetails
              statutoryName={examListingData.candidateName}
              nricFin={examListingData.candidateId}
              citizenship={examListingData.candidateCitizenship}
              schoolName={examListingData.examSchool}
              indexNo={examListingData.candidateIndexNum}
            />
            <Divider />
            <ExamDetails
              examLevel={examListingData.examLevel}
              examSeries={examListingData.examSeries}
              examYear={examListingData.examYear.toString()}
            />
            {examListingData.subjectResultModels && (
              <Box className={classes.dataTableContainer}>
                <SubjectSelectionTable
                  examData={examListingData.subjectResultModels}
                  selectedSubjects={selectionArr}
                  onSelectSubject={setSelectionArr}
                />
              </Box>
            )}
          </ContentTemplate>
          <ContentTemplate continuous>
            {errMsg && (
              <Grid xs={12} md={8}>
                <Typography className={classes.errorText} display="inline" variant="body2">
                  {t('appeal->>SelectSubject.There was an error. Please try again.')}
                </Typography>
              </Grid>
            )}
            <Grid
              className={classes.buttonContainer}
              container
              direction="row"
              alignItems="flex-start"
              spacing={3}
            >
              <Grid xs={12} md={3}>
                <Button
                  id="button-selectSubject-back"
                  fullWidth
                  className={classes.button}
                  variant="outlined"
                  onClick={() => {
                    onBack();
                  }}
                >
                  {t('appeal:SelectSubject.Back')}
                </Button>
              </Grid>

              <Grid xs={12} md={5}>
                <ApiButton
                  id="button-selectSubject-continueToNextPage"
                  status={proceedStatus}
                  fullWidth
                  className={classes.button}
                  onClick={handleProceedClick}
                  disabled={selectionArr.length === 0}
                >
                  {t(`appeal:SelectSubject.Proceed with {{subjectCount}} Subject(s)`, {
                    subjectCount: selectionArr.length,
                  })}
                </ApiButton>
              </Grid>
            </Grid>
          </ContentTemplate>
        </Box>
      )}
    </>
  );
};

export default SelectSubject;
